import * as Api from '@ViewModels';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { invalidateEventAutomationCoverage } from '../../../Surveys/useEventAutomationCoverage';
import { AUTOMATION_QUERY_KEYS, AUTOMATION_TEMPLATE_MUTATION_KEY } from '../../queryKeys';
import { invalidateAllAutomationTemplatesQueries } from '../../queries/useAutomationTemplatesQuery';

interface MutationVariables {
	id: string;
	status: Api.TemplateStatus;
	impersonationContext?: Api.IImpersonationContext;
}

/**
 * Hook for setting the status of an automation template.
 *
 * This mutation updates the template's operational status, controlling its availability
 * and behavior. Valid statuses include:
 * - Published: Active and available for use
 * - Disabled: Temporarily deactivated
 * - Draft: Under development/editing
 * - Archived: Permanently deactivated and hidden from normal views
 *
 * @param options - Configuration options for the mutation
 * @param options.onSuccess - Callback fired when the mutation succeeds
 * @param options.onError - Callback fired when the mutation fails
 * @returns A React Query mutation object
 */
export function useAutomationTemplateSetStatusMutation({
	onSuccess,
	onError,
}: {
	onSuccess?: (data: Api.IAutomationTemplate, variables: MutationVariables) => void;
	onError?: (error: Api.IOperationResultNoValue) => void;
} = {}) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: AUTOMATION_TEMPLATE_MUTATION_KEY,
		mutationFn: ({ id, status, impersonationContext }: MutationVariables) => {
			return userSession.webServiceHelper.callAsync<Api.IAutomationTemplate>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `AutomationTemplate/${encodeURIComponent(id)}/Status`,
					impersonationContext,
					queryParams: {
						status,
					},
				}),
				'PUT'
			);
		},
		onError,
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({ queryKey: AUTOMATION_QUERY_KEYS.template(variables.id) });
			invalidateAllAutomationTemplatesQueries();
			invalidateEventAutomationCoverage();
			onSuccess?.(data, variables);
		},
	});
}
